import React, { useState } from "react";
//@ts-ignore
import i18n from "../i18n.js";
import Box from "@mui/material/Box";
import LinkMui from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Popover from "@mui/material/Popover";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import Tooltip from "@mui/material/Tooltip";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import LocationOnIcon from "@mui/icons-material/LocationOn";

type JudgeType = "judge" | "scrutineer" | "chairperson";

interface JudgeClasses {
    judge: string[];
    scrutineer: string[];
    chairperson: string[];
    wdsf: string | null;
    wdsf_pd: string | null;
    wdsf_scrutineer: string | null;
    wdsf_chairperson: string | null;
}

interface JudgeContact {
    phone: string | null;
    email: string | null;
}

interface JudgeLicences {
    judge: Record<string, string>;
    scrutineer: Record<string, string>;
    chairperson: Record<string, string>;
}

export interface Judge {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    country: string;
    city: string | null;
    federation: string | null;
    classes: JudgeClasses;
    contact: JudgeContact;
    licences: JudgeLicences;
    uuid: string;
}

interface RootItemOptions {
    pzst: {
        judges: Judge[];
    };
    fts?: unknown;
    api?: unknown;
}

export interface JudgesListProps {
    itemOptions: RootItemOptions;
    type: JudgeType;
}

interface TFunction {
    (key: string, options?: Record<string, unknown>): string;
}

const Category = ({ cat }: { cat: string }) => {
    let color: "default" | "error" | "secondary" | "warning" | "primary" = "default";
    let label = cat.replace("PZST-", "");
    if (cat.includes("WDSF") && cat.includes("-A")) {
        color = "error";
    } else if (cat.includes("WDSF") && cat.includes("-B")) {
        color = "secondary";
    } else if (cat.includes("-TWO") || cat.includes("-AJS")) {
        color = "default";
    } else if (cat.includes("PZST") && (cat.includes("-B") || cat.includes("-SCR-II"))) {
        color = "warning";
    } else if (cat.includes("PZST") && (cat.includes("-A") || cat.includes("-SCR-I"))) {
        color = "primary";
    }
    return <Chip size="small" color={color} sx={{ m: 0.25 }} variant="outlined" label={label} />;
};

const Judge = ({ j, t, type }: { j: Judge; t: TFunction; type: JudgeType }) => {
    const year = new Date().getFullYear();
    let textColor = "text.primary";
    let tt = "";
    if (j.licences[type][year] === undefined) {
        textColor = "error.main";
        tt = `${t("adjudicator.no_licence_for_year")} ${year}`;
    }
    return (
        <Box sx={{ borderBottom: "thin solid", pb: 1, color: textColor }}>
            <Stack direction="column">
                <Stack direction="row">
                    <Box sx={{ flexGrow: 1 }}>
                        <Tooltip title={tt} placement="bottom-start">
                            <Typography variant="subtitle1" sx={{ marginRight: 1 }}>{`${j.lastName} ${j.firstName}`}</Typography>
                        </Tooltip>
                        {j.city && (
                            <Stack direction="row" alignItems="center" sx={{ color: "text.secondary" }}>
                                <LocationOnIcon fontSize="small" />
                                <Typography variant="body2" sx={{ pl: 0.5 }}>
                                    {j.city}
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                    <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        <Stack direction="row" sx={{ flexWrap: "wrap" }}>
                            {j.classes[type].map(cat => (
                                <Category cat={cat} key={`1${j.id}${cat}`} />
                            ))}
                            {type == "judge" && j.classes.wdsf !== null && <Category cat={j.classes.wdsf} />}
                            {type == "judge" && j.classes.wdsf_pd !== null && <Category cat={j.classes.wdsf_pd} />}
                            {type == "scrutineer" && j.classes.wdsf_scrutineer !== null && <Category cat={j.classes.wdsf_scrutineer} />}
                            {type == "chairperson" && j.classes.wdsf_chairperson !== null && <Category cat={j.classes.wdsf_chairperson} />}
                        </Stack>
                    </Box>
                    <Box>
                        <ContactButton t={t} j={j} />
                    </Box>
                </Stack>
                <Stack direction="row" sx={{ flexWrap: "wrap", display: { xs: "block", sm: "none" } }}>
                    {j.classes[type].map(cat => (
                        <Category cat={cat} key={`2${j.id}${cat}`} />
                    ))}
                    {type == "judge" && j.classes.wdsf !== null && <Category cat={j.classes.wdsf} />}
                    {type == "judge" && j.classes.wdsf_pd !== null && <Category cat={j.classes.wdsf_pd} />}
                    {type == "scrutineer" && j.classes.wdsf_scrutineer !== null && <Category cat={j.classes.wdsf_scrutineer} />}
                    {type == "chairperson" && j.classes.wdsf_chairperson !== null && <Category cat={j.classes.wdsf_chairperson} />}
                </Stack>
            </Stack>
        </Box>
    );
};

const ContactButton = ({ j, t }: { j: Judge; t: TFunction }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? `${j.id}_contact_details` : undefined;
    const pt2 = j.contact.phone != null ? 0 : 2;
    return (
        <React.Fragment>
            <IconButton aria-describedby={id} aria-label="contact" onClick={handleClick} size="small">
                <ContactPageIcon fontSize="small" />
            </IconButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "center"
                }}
            >
                <Stack direction="column">
                    {j.contact.phone != null && (
                        <Stack direction="row" sx={{ pb: 2, pt: 2, px: 2 }}>
                            <ContactPhoneIcon />
                            <LinkMui href={`tel:${j.contact.phone}`}>
                                <Typography sx={{ pl: 1 }}>{j.contact.phone}</Typography>
                            </LinkMui>
                        </Stack>
                    )}
                    {j.contact.email != null && (
                        <Stack direction="row" sx={{ pb: 2, px: 2, pt: pt2 }}>
                            <ContactMailIcon />
                            <LinkMui href={`mailto:${j.contact.email}`}>
                                <Typography sx={{ pl: 1 }}>{j.contact.email}</Typography>
                            </LinkMui>
                        </Stack>
                    )}
                </Stack>
            </Popover>
        </React.Fragment>
    );
};

const Root: React.FC<JudgesListProps> = ({ itemOptions, type }) => {
    const { pzst } = itemOptions;
    const { t } = i18n;

    const [judges] = useState<Judge[]>(pzst.judges);

    return (
        <React.Fragment>
            <Box sx={{ minHeight: "100vh" }}>
                <Box sx={{ flexGrow: 1, bgcolor: "background.paper", display: "flex", flexDirection: "column" }}>
                    <Stack spacing={1}>
                        {judges?.map(j => (
                            <Judge key={`j-${j.id}`} j={j} t={t} type={type} />
                        ))}
                    </Stack>
                </Box>
            </Box>
        </React.Fragment>
    );
};

export default Root;
